import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, SlidersHorizontal, X, Loader2 } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import ProductCard from '../components/ProductCard';
import { fetchCategories, fetchProducts } from '../lib/api';

const MAX_PRICE = 1500;

const Category = () => {
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const [categories, setCategories] = useState([]);
  const [sort, setSort] = useState('relevance');
  const [priceRange, setPriceRange] = useState([0, MAX_PRICE]);
  const [selectedBrands, setSelectedBrands] = useState([]);

  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  // Reset filters when the category or query changes
  useEffect(() => {
    setSelectedBrands([]);
    setPriceRange([0, MAX_PRICE]);
  }, [categoryId, query]);

  useEffect(() => {
    setLoading(true);
    let ignore = false;
    const t = setTimeout(() => {
      const params = { sort, min_price: priceRange[0], max_price: priceRange[1] };
      if (categoryId && categoryId !== 'tous') params.category = categoryId;
      if (query) params.q = query;
      if (selectedBrands.length) params.brand = selectedBrands.join(',');
      fetchProducts(params)
        .then((d) => { if (ignore) return; setProducts(d.items); setBrands(d.brands); setTotal(d.total); setLoading(false); })
        .catch(() => { if (ignore) return; setProducts([]); setLoading(false); });
    }, 300);
    return () => { ignore = true; clearTimeout(t); };
  }, [categoryId, query, sort, priceRange, selectedBrands]);

  const category = categories.find((c) => c.id === categoryId);
  const title = query ? `Résultats pour « ${query} »` : (category?.name || 'Tous les produits');

  const toggleBrand = (brand) => {
    setSelectedBrands((prev) => (prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]));
  };

  const filtersActive = selectedBrands.length > 0 || priceRange[0] > 0 || priceRange[1] < MAX_PRICE;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="category-page">
      <nav className="flex items-center gap-1 text-sm text-[#595959] mb-4">
        <Link to="/" className="hover:text-[#00847A]">Accueil</Link>
        <ChevronRight size={14} />
        <span className="text-[#262626] font-medium">{title}</span>
      </nav>

      <h1 className="text-3xl md:text-4xl font-black text-[#262626] mb-2" data-testid="category-title">{title}</h1>
      <p className="text-[#595959] mb-6" data-testid="category-count">{total} produits disponibles</p>

      {category?.subcategories && !query && (
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
          {category.subcategories.map((sub) => (
            <span key={sub} className="px-4 py-2 bg-neutral-100 text-sm rounded-full whitespace-nowrap text-[#262626]">
              {sub}
            </span>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-[260px_1fr] gap-6">
        {/* Filters */}
        <aside className="bg-white border border-neutral-200 rounded-xl p-5 h-fit sticky top-32">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-[#262626] flex items-center gap-2"><SlidersHorizontal size={18} /> Filtres</h3>
            {filtersActive && (
              <button data-testid="reset-filters" onClick={() => { setSelectedBrands([]); setPriceRange([0, MAX_PRICE]); }} className="text-xs text-[#00847A] hover:underline">Réinitialiser</button>
            )}
          </div>

          <div className="mb-6">
            <h4 className="font-semibold text-sm mb-3 text-[#262626]">Prix</h4>
            <Slider value={priceRange} min={0} max={MAX_PRICE} step={10} onValueChange={setPriceRange} className="[&_[role=slider]]:bg-[#00847A] [&_[role=slider]]:border-[#00847A]" data-testid="price-slider" />
            <div className="flex justify-between text-xs text-[#595959] mt-2">
              <span>{priceRange[0]}€</span>
              <span>{priceRange[1]}€</span>
            </div>
          </div>

          {brands.length > 0 && (
            <div className="mb-6">
              <h4 className="font-semibold text-sm mb-3 text-[#262626]">Marques</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {brands.map((b) => (
                  <label key={b} className="flex items-center gap-2 cursor-pointer text-sm text-[#262626] hover:text-[#00847A]">
                    <Checkbox checked={selectedBrands.includes(b)} onCheckedChange={() => toggleBrand(b)} data-testid={`brand-${b}`} className="data-[state=checked]:bg-[#00847A] data-[state=checked]:border-[#00847A]" />
                    {b}
                  </label>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* Product list */}
        <div>
          <div className="flex items-center justify-between mb-4 bg-neutral-50 rounded-lg p-3">
            <span className="text-sm text-[#595959]">Trier par :</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select" className="text-sm bg-white border border-neutral-300 rounded-md px-3 py-1.5 focus:outline-none focus:border-[#00847A]">
              <option value="relevance">Pertinence</option>
              <option value="price_asc">Prix croissant</option>
              <option value="price_desc">Prix décroissant</option>
              <option value="rating">Meilleures notes</option>
              <option value="discount">Promotions</option>
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-[#00847A]"><Loader2 size={32} className="animate-spin" /></div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-[#595959]" data-testid="no-results">
              <X size={48} className="mx-auto mb-4" />
              <p>Aucun produit ne correspond à vos critères.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-grid">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Category;
